import { split } from '@chakra-ui/object-utils'
import type {
  HTMLChakraProps,
  SystemStyleObject,
  ThemingProps} from '@chakra-ui/react'
import {
  chakra,
  Checkbox,
  FormLabel,
  forwardRef,
  Icon,
  layoutPropNames,
  omitThemingProps,
  Tooltip,
  useMultiStyleConfig,
} from '@chakra-ui/react'
import {
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  useState,
} from 'react'

export interface CheckboxSelectItemProps {
  label: string
  value: string
  tooltipText?: string
  defaultSelected?: boolean
  isSelected?: boolean
}

interface CheckboxSelectProps extends ThemingProps {
  options: CheckboxSelectItemProps[]
  prompt?: string
  canUnselect?: boolean
  onChange: (value: string) => void
}

export const CheckboxSelect = forwardRef<CheckboxSelectProps, 'select'>(
  (
    {
      options,
      prompt = 'Vyberte ze seznamu...',
      canUnselect = false,
      onChange,
      ...props
    },
    ref,
  ) => {
    const wrapperRef = useRef<HTMLDivElement>(null)
    const selectBoxRef = useRef<HTMLDivElement>(null)
    const [isOpen, setOpen] = useState(false)
    const [_options, _setOptions] = useState<CheckboxSelectItemProps[]>([])

    useEffect(() => {
      const newOptions = options.map((option) => {
        return {
          ...option,
          isSelected: option.defaultSelected || false,
        }
      })

      _setOptions(newOptions)
    }, [options])

    const getSelected = (selectedOptions: CheckboxSelectItemProps[]) => {
      return selectedOptions.find((option) => option.isSelected)
    }

    const selectItems = (value: string) => {
      return _options.map((item) => {
        // Select or unselect (if canUnselect) the clicked option
        if (item.value === value) {
          return {
            ...item,
            isSelected: canUnselect ? !item.isSelected : true,
          }
        }

        // Unselect all other options
        return {
          ...item,
          isSelected: false,
        }
      })
    }

    const handleOpen = () => {
      setOpen(true)
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target

      const newSelectedOptions = selectItems(value)
      const selected = getSelected(newSelectedOptions)

      if (selected && onChange) {
        onChange(selected.value)
      }

      if (newSelectedOptions) {
        _setOptions(newSelectedOptions)
        setOpen(false)
      }
    }

    const selected = getSelected(_options)

    const styles = useMultiStyleConfig('Select', props)

    const rest = omitThemingProps(props)

    const [layoutProps] = split(rest, layoutPropNames as any[])

    const rootStyles: SystemStyleObject = {
      display: 'grid',
      gap: 2,
      width: 'max-content',
      height: 'fit-content',
      position: 'relative',
    }

    const fieldStyles: SystemStyleObject = {
      ...styles.field,
      width: '100%',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      _focus: {
        zIndex: 'unset',
        ...(styles as any).field?.['_focus'],
      },
    }

    const selectBoxStyles: SystemStyleObject = isOpen
      ? {
        ...styles.field,
        display: 'grid',
        height: 'auto',
        gap: 2,
        whiteSpace: 'nowrap',
        width: '100%',
        _focus: {
          zIndex: 'unset',
          ...(styles as any).field?.['_focus'],
        },
      }
      : {
        display: 'grid',
        height: 0,
        overflow: 'hidden',
      }

    return (
      <chakra.div
        ref={wrapperRef}
        className="checkbox-select__wrapper"
        __css={rootStyles}
        {...layoutProps}
      >
        <Tooltip label={selected ? selected.tooltipText : undefined}>
          <chakra.div onClick={handleOpen} __css={fieldStyles}>
            {selected ? selected.label : prompt}

            <SelectIcon />
          </chakra.div>
        </Tooltip>

        <chakra.div __css={selectBoxStyles} ref={selectBoxRef}>
          {_options &&
            _options.length > 0 &&
            _options.map((option) => {
              const isSelected = option.isSelected

              return (
                <Tooltip key={option.value} label={option.tooltipText}>
                  <FormLabel>
                    <Checkbox
                      value={option.value}
                      isChecked={isSelected}
                      sx={{ whiteSpace: 'nowrap' }}
                      onChange={handleChange}
                    >
                      {option.label}
                    </Checkbox>
                  </FormLabel>
                </Tooltip>
              )
            })}
        </chakra.div>
      </chakra.div>
    )
  },
)

const IconWrapper = chakra('div', {
  baseStyle: {
    position: 'absolute',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    top: '50%',
    right: 2,
    transform: 'translateY(-50%)',
  },
})

type SelectIconProps = HTMLChakraProps<'div'>

const SelectIcon: React.FC<SelectIconProps> = (props) => {
  // const { children = <DefaultIcon />, ...rest } = props
  const { children = <Icon />, ...rest } = props

  const clone = cloneElement(children as any, {
    role: 'presentation',
    className: 'chakra-select__icon',
    focusable: false,
    'aria-hidden': true,
    // force icon to adhere to `IconWrapper` styles
    style: {
      width: '1em',
      height: '1em',
      color: 'currentColor',
    },
  })

  return (
    <IconWrapper {...rest} className="chakra-select__icon-wrapper">
      {isValidElement(children) ? clone : null}
    </IconWrapper>
  )
}

SelectIcon.displayName = 'SelectIcon'
